/**
 * Creates a category. The header's primary button and the panel's footer link both mount one
 * of these, each with its own uncontrolled open state; the icon is chosen afterwards on its row.
 */
import { useId, useState, type FormEvent, type ReactNode } from 'react';

import { FormDialog } from '../../../chrome/form';
import { Field, FieldLabel } from '../../../ui/field';
import { Input } from '../../../ui/input';
import { useCan } from '../../session/orgContext';
import { useCreateCategory } from './api/queries';
import { tgt } from './messages';

export function NewCategory(props: { campaignId: string; trigger: ReactNode }): ReactNode {
  const canWrite = useCan('control_plane.write');
  const createMutation = useCreateCategory(props.campaignId);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const nameId = useId();

  function handleOpenChange(next: boolean): void {
    setOpen(next);
    if (!next) {
      setName('');
      createMutation.reset();
    }
  }

  function submit(event: FormEvent<HTMLFormElement>): void | Promise<unknown> {
    event.preventDefault();
    const trimmed = name.trim();
    if (trimmed === '') return;
    return createMutation.mutateAsync({ name: trimmed });
  }

  if (!canWrite) return null;

  return (
    <FormDialog
      open={open}
      onOpenChange={handleOpenChange}
      trigger={props.trigger}
      title={tgt('create.title')}
      submitLabel={tgt('create.submit')}
      pending={createMutation.isPending}
      submitDisabled={name.trim() === ''}
      failure={createMutation.error?.failure ?? null}
      onSubmit={submit}
    >
      <Field>
        <FieldLabel htmlFor={nameId}>{tgt('create.label')}</FieldLabel>
        <Input id={nameId} value={name} onChange={(event) => setName(event.target.value)} />
      </Field>
    </FormDialog>
  );
}
